import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ScrollText, AlertTriangle, CheckCircle2, Plus, Clock, ShieldAlert,
  Trash2, Zap,
} from "lucide-react";
import { toast } from "sonner";

// Mounts inside ProjectDashboard for TV projects AND inside ShowDeskDetail.
// Anyone with read access on the project can view the log; only
// authorized pushers (PH owner, head writer, or channel programming
// head / S&P) can create a change.
export default function ScriptChangeLog({ projectId, viewerRole = "ph_owner" }) {
  const [changes, setChanges] = useState([]);
  const [urgentPending, setUrgentPending] = useState(0);
  const [shootReady, setShootReady] = useState({ blocked: false, blockers: [] });
  const [loading, setLoading] = useState(true);
  const [depts, setDepts] = useState([]);
  const [pushOpen, setPushOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: gate }] = await Promise.all([
        api.get(`/projects/${projectId}/script-changes`),
        api.get(`/projects/${projectId}/shoot-ready`),
      ]);
      setChanges(list.changes || []);
      setUrgentPending(list.urgent_pending || 0);
      setShootReady(gate);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [projectId]);
  useEffect(() => {
    api.get("/meta/departments").then(({ data }) => setDepts(data || []))
      .catch(() => setDepts([]));
  }, []);

  const canPush = ["ph_owner", "admin", "channel_owner", "desk_member"]
    .includes(viewerRole);

  const acknowledge = async (id) => {
    try {
      await api.post(`/projects/${projectId}/script-changes/${id}/acknowledge`,
                     { note: "" });
      toast.success("Acknowledged");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not ack");
    }
  };

  const withdraw = async (id) => {
    if (!confirm("Withdraw this script change? It stays audited but not enforced.")) return;
    try {
      await api.delete(`/projects/${projectId}/script-changes/${id}`);
      toast.success("Withdrawn");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not withdraw");
    }
  };

  return (
    <div className="cs-card rounded-2xl p-6" data-testid="script-log-panel">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
            Script Change Log
          </div>
          <h3 className="font-display text-2xl uppercase tracking-tight mt-1">
            {urgentPending > 0 ? `${urgentPending} Urgent Pending` : "All Clear"}
          </h3>
          <div className="mt-1 text-xs text-white/55">
            HODs must acknowledge urgent changes before shoot resumes.
          </div>
        </div>
        {canPush && (
          <Button onClick={() => setPushOpen(true)}
            className="btn-themed h-11 px-5 rounded-full text-xs uppercase tracking-[0.25em]"
            data-testid="script-push-btn">
            <Zap size={13} className="mr-1.5" /> Push change
          </Button>
        )}
      </div>

      {/* Shoot-Ready gate banner */}
      {shootReady.blocked ? (
        <div className="rounded-xl p-4 border border-red-500/40 bg-red-500/[0.07] flex items-start gap-3 mb-5"
          data-testid="shoot-ready-blocked">
          <ShieldAlert size={16} className="text-red-300 shrink-0 mt-0.5" />
          <div className="text-sm text-red-100">
            <b className="text-red-300">Shoot on hold.</b>{" "}
            {shootReady.blockers.length} urgent change{shootReady.blockers.length > 1 ? "s" : ""} awaiting HOD acknowledgment.
          </div>
        </div>
      ) : (
        <div className="rounded-xl p-4 border border-emerald-500/25 bg-emerald-500/[0.05] flex items-start gap-3 mb-5"
          data-testid="shoot-ready-clear">
          <CheckCircle2 size={16} className="text-emerald-300 shrink-0 mt-0.5" />
          <div className="text-sm text-emerald-100">
            <b className="text-emerald-300">Shoot cleared.</b> No urgent changes pending.
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-white/45 py-8 text-center">Loading changes…</div>
      ) : changes.length === 0 ? (
        <div className="text-sm text-white/45 py-10 text-center"
          data-testid="script-log-empty">
          No script changes logged yet.
        </div>
      ) : (
        <div className="space-y-3" data-testid="script-log-list">
          {changes.map((c) => (
            <ChangeRow key={c.id} c={c}
              onAck={() => acknowledge(c.id)}
              onWithdraw={() => withdraw(c.id)}
              viewerRole={viewerRole} />
          ))}
        </div>
      )}

      <PushDialog open={pushOpen} onOpenChange={setPushOpen}
        projectId={projectId} departments={depts} onPushed={load} />
    </div>
  );
}

function ChangeRow({ c, onAck, onWithdraw, viewerRole }) {
  const isUrgent = c.urgency === "urgent";
  const acksProgress = c.required_count === 0
    ? 100 : Math.round((c.acked_required / c.required_count) * 100);
  const gateOpen = c.required_count === 0
    || c.acked_required >= c.required_count;
  const canWithdraw = ["ph_owner", "admin"].includes(viewerRole);

  return (
    <div className={"rounded-xl p-4 border transition-colors " +
      (isUrgent
        ? "border-red-500/25 bg-red-500/[0.03]"
        : "border-white/10 bg-white/[0.03]")}
      data-testid={`script-change-row-${c.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className={"w-10 h-10 rounded-lg flex items-center justify-center shrink-0 " +
          (isUrgent ? "bg-red-500/15 text-red-300" : "bg-white/8 text-white/75")}>
          {isUrgent ? <AlertTriangle size={16} /> : <ScrollText size={16} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isUrgent && (
              <span className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-[0.22em] bg-red-500/20 text-red-200">
                Urgent
              </span>
            )}
            <span className="text-sm font-semibold text-white">{c.title}</span>
            {c.scene_ref && (
              <span className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                Scene {c.scene_ref}
              </span>
            )}
            {c.page_ref && (
              <span className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                Page {c.page_ref}
              </span>
            )}
          </div>
          <div className="mt-1.5 text-[11px] text-white/55">
            {c.origin_label || c.pushed_by_name} · pushed {c.pushed_at?.slice(0, 16).replace("T", " ")}
            {c.departments?.length > 0 && ` · ${c.departments.join(" · ")}`}
          </div>
          <div className="mt-2 text-sm text-white/80 whitespace-pre-line line-clamp-3">
            {c.body}
          </div>

          {/* Ack progress */}
          {c.required_count > 0 && (
            <div className="mt-3">
              <div className="flex items-center justify-between text-[10px] tracking-[0.22em] uppercase text-white/55 mb-1">
                <span>HOD Acknowledgments</span>
                <span className={gateOpen ? "text-emerald-300" : "text-red-300"}>
                  {c.acked_required} / {c.required_count}
                </span>
              </div>
              <div className="h-1.5 bg-white/6 rounded-full overflow-hidden">
                <div
                  className={"h-full rounded-full transition-all " +
                    (gateOpen ? "bg-emerald-400" : "bg-red-400")}
                  style={{ width: `${acksProgress}%` }} />
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col items-end gap-1.5">
          {c.viewer_needs_to_ack && (
            <Button onClick={onAck}
              className="h-8 px-3 rounded-full text-[10px] uppercase tracking-[0.22em] bg-emerald-500/80 hover:bg-emerald-500 text-white"
              data-testid={`script-ack-btn-${c.id}`}>
              <CheckCircle2 size={11} className="mr-1" /> Acknowledge
            </Button>
          )}
          {canWithdraw && (
            <button onClick={onWithdraw}
              className="text-white/40 hover:text-red-300 transition-colors p-1"
              data-testid={`script-withdraw-${c.id}`}
              aria-label="Withdraw">
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PushDialog({ open, onOpenChange, projectId, departments, onPushed }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [urgency, setUrgency] = useState("urgent");
  const [route, setRoute] = useState("via_writer");
  const [sceneRef, setSceneRef] = useState("");
  const [pageRef, setPageRef] = useState("");
  const [depts, setDepts] = useState([]);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setTitle(""); setBody(""); setUrgency("urgent");
    setRoute("via_writer"); setSceneRef(""); setPageRef(""); setDepts([]);
  };

  const toggleDept = (k) => {
    setDepts((prev) => prev.includes(k)
      ? prev.filter((x) => x !== k) : [...prev, k]);
  };

  const submit = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    if (depts.length === 0) {
      toast.error("Pick at least one department to target");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/script-changes`, {
        title: title.trim(), body: body.trim(),
        urgency, route,
        scene_ref: sceneRef.trim(),
        page_ref: pageRef.trim(),
        departments: depts,
      });
      toast.success(urgency === "urgent"
        ? "Urgent change pushed — HODs will see the shoot-hold banner"
        : "Change logged");
      onOpenChange(false); reset(); onPushed?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not push");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="bg-[#0a0a0c] border border-white/10 text-white max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="script-push-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-lg">
            Push a script change
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            HODs in the targeted departments must acknowledge urgent changes
            before the Shoot-Ready gate reopens.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                Urgency
              </Label>
              <Select value={urgency} onValueChange={setUrgency}>
                <SelectTrigger className="mt-2 h-11 bg-black/40 border-white/15"
                  data-testid="script-urgency-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0c] border-white/15 text-white">
                  <SelectItem value="urgent">Urgent — shoot on hold</SelectItem>
                  <SelectItem value="routine">Routine — logged only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                Route
              </Label>
              <Select value={route} onValueChange={setRoute}>
                <SelectTrigger className="mt-2 h-11 bg-black/40 border-white/15"
                  data-testid="script-route-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0c] border-white/15 text-white">
                  <SelectItem value="via_writer">Via writer</SelectItem>
                  <SelectItem value="direct_to_hods">Direct to HODs (S&P override)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Title *
            </Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Recut scene 12 — remove ancestor reference"
              className="mt-2 h-11 bg-black/40 border-white/15"
              data-testid="script-title-input" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                Scene ref (optional)
              </Label>
              <Input value={sceneRef} onChange={(e) => setSceneRef(e.target.value)}
                placeholder="Sc 12A"
                className="mt-2 h-11 bg-black/40 border-white/15"
                data-testid="script-scene-input" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                Page ref (optional)
              </Label>
              <Input value={pageRef} onChange={(e) => setPageRef(e.target.value)}
                placeholder="p. 47"
                className="mt-2 h-11 bg-black/40 border-white/15"
                data-testid="script-page-input" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Change details *
            </Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)}
              rows={6}
              placeholder="Spell out the change: what was, what should be, and why."
              className="mt-2 bg-black/40 border-white/15 text-sm"
              data-testid="script-body-input" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Target departments * ({depts.length} selected)
            </Label>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2"
              data-testid="script-dept-picker">
              {departments.map((d) => (
                <button key={d.key} type="button"
                  onClick={() => toggleDept(d.key)}
                  className={"px-3 py-2 text-[10px] tracking-[0.22em] uppercase rounded-lg border transition-colors " +
                    (depts.includes(d.key)
                      ? "border-[color:var(--association-theme)] bg-[color:var(--association-theme)]/10 text-white"
                      : "border-white/15 text-white/60 hover:border-white/30 hover:text-white")}
                  data-testid={`script-dept-${d.key}`}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => onOpenChange(false)}
              className="h-11 px-5 text-xs uppercase tracking-[0.22em]"
              data-testid="script-push-cancel">
              Cancel
            </Button>
            <Button onClick={submit}
              disabled={saving || !title.trim() || !body.trim() || depts.length === 0}
              className="btn-themed h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em]"
              data-testid="script-push-submit">
              {saving ? "Pushing…" : "Push to HODs"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
